import { AssignmentModel, AssignmentViewModel, CaseTypeEnum } from 'api/model';
import React from 'react';
import { getHoursAndMinutesFromMillisecondsString } from 'utils/date-helpers';
import { isBreakOnTopForLeg } from 'utils/breakOnTopPlanning';
import { getElectricCarBufferLabel } from 'utils/electricCarBuffer';
import useResultingStartAndEndTime from './useResultingStartAndEndTimes';

interface Props {
  assignment: AssignmentModel;
  caseType: CaseTypeEnum;
  sameTimePurchaseWithEstimatedDuration?: AssignmentViewModel;
}

const EstimatedAssignmentTimeValue: React.FC<Props> = ({
  assignment,
  caseType,
  sameTimePurchaseWithEstimatedDuration,
}) => {
  const {
    fixedAssignmentTime,
    estimatedDuration,
    breakTime,
    bookedTo,
    estimatedStartTime,
    assignmentTypeID,
  } = assignment;
  // From the planning rollout date onward, regular EstimatedDuration is stored as the occupied window
  // (drive + break). This row shows the pure DRIVE core (= window − break), mirroring fixed-time which shows
  // fixedAssignmentTime. Before the rollout (by BookedTo), EstimatedDuration is the drive estimate as-is.
  // The total (drive + break) is shown in the separate "Estimerad tid" row. Passenger legs keep the trip time
  // as-is: the break is taken during the trip, it is not on top of it.
  const driveDuration =
    fixedAssignmentTime ||
    estimatedDuration === undefined ||
    !isBreakOnTopForLeg(bookedTo, assignmentTypeID, caseType)
      ? estimatedDuration
      : Math.max(0, estimatedDuration - Math.round((breakTime ?? 0) * 60 * 1000));
  // Laddbufferten (server-persisterad) är belagd tid ovanpå estimatet: den ska in i
  // start-/slutspannet (backendens härledda tider räknar med den) men inte i den visade
  // körtidssiffran — där visas den som separat "+30 min (elbil)"-etikett i stället.
  const electricCarBufferTime = assignment.electricCarBufferTime ?? 0;
  const electricCarBufferLabel = getElectricCarBufferLabel(
    electricCarBufferTime
  );
  const { startAndEndTimeString } = useResultingStartAndEndTime({
    bookedTo,
    estimatedWorkTime:
      driveDuration != null
        ? driveDuration + electricCarBufferTime
        : driveDuration,
    estimatedStartTime,
    caseType,
    assignmentType: assignmentTypeID,
  });
  const { startAndEndTimeString: sameTimePurchaseStartAndEndTimeString } =
    useResultingStartAndEndTime({
      bookedTo,
      caseType,
      assignmentType:
        sameTimePurchaseWithEstimatedDuration?.assignment.assignmentTypeID,
      estimatedWorkTime:
        sameTimePurchaseWithEstimatedDuration?.assignment.estimatedDuration,
      estimatedStartTime:
        sameTimePurchaseWithEstimatedDuration?.assignment.estimatedStartTime,
    });

  return fixedAssignmentTime ? (
    <span>{getHoursAndMinutesFromMillisecondsString(fixedAssignmentTime)}</span>
  ) : driveDuration ? (
    <span>
      {getHoursAndMinutesFromMillisecondsString(driveDuration)}
      {electricCarBufferLabel && (
        <span
          style={{ marginLeft: 6, opacity: 0.75 }}
          title="Automatiskt tillägg på estimerad tid för att ta höjd för laddning av elbil"
        >
          {electricCarBufferLabel}
        </span>
      )}
      {startAndEndTimeString && ` (${startAndEndTimeString})`}
    </span>
  ) : sameTimePurchaseWithEstimatedDuration?.assignment.estimatedDuration ? (
    <span style={{ opacity: 0.5 }}>
      {getHoursAndMinutesFromMillisecondsString(
        sameTimePurchaseWithEstimatedDuration.assignment.estimatedDuration
      )}
      {` (${sameTimePurchaseStartAndEndTimeString}, samma som ${sameTimePurchaseWithEstimatedDuration.case.registrationNumber})`}
    </span>
  ) : null;
};

export default EstimatedAssignmentTimeValue;
